// src/Product/Accounting/ManagementReport.js
import React, { useRef, useState, useCallback, useMemo } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import BalanceSheet from "./sofp";
import CashFlows from "./cashFlows";
import "./ManagementReport.css";

import jsPDF from "jspdf";
import "jspdf-autotable";
import html2canvas from "html2canvas";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
} from "chart.js";
import { Line, Pie, Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
);

// ---------------- Helpers ----------------
const monthKey = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabelShort = (yyyyMm) => {
  // yyyyMm expected like "2025-01"
  const [yy, mm] = String(yyyyMm || "").split("-");
  const m = Number(mm || 1) - 1;
  const dt = new Date(Number(yy || 2000), Math.max(0, Math.min(11, m)), 1);
  return dt.toLocaleString(undefined, { month: "short" }); // "Jan"
};

const startOfYear = (dStr) => {
  const d = new Date(dStr);
  const y = d.getFullYear();
  return `${y}-01-01`;
};

const isISAccountType = (t) =>
  ["Revenue", "Cost of Sales", "Expense"].includes(String(t || ""));

// Proper sign per account type (journal-only)
// Revenue: credit - debit (positive)
// COS/Expense: debit - credit (positive)
const amountForPL = (type, debit, credit) => {
  const t = String(type || "");
  const dr = Number(debit) || 0;
  const cr = Number(credit) || 0;

  if (t === "Revenue") return cr - dr;
  if (t === "Cost of Sales") return dr - cr;
  if (t === "Expense") return dr - cr;
  return 0;
};

//const sum = (arr) => arr.reduce((a, b) => a + (Number(b) || 0), 0);

// 10,000,000 -> 10M ; 10,000 -> 10K ; supports negatives
const formatCompact = (n) => {
  const x = Number(n) || 0;
  const abs = Math.abs(x);

  const round1 = (v) => {
    // keep at most 1 decimal when needed (e.g. 1.2M)
    const r = Math.round(v * 10) / 10;
    return Number.isInteger(r) ? String(r) : String(r);
  };

  if (abs >= 1_000_000_000)
    return `${x < 0 ? "-" : ""}${round1(abs / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${x < 0 ? "-" : ""}${round1(abs / 1_000_000)}M`;
  if (abs >= 1_000) return `${x < 0 ? "-" : ""}${round1(abs / 1_000)}K`;
  return `${x}`;
};

const currency = (n) => `₦${Number(n || 0).toLocaleString()}`;

// Get month start/end of a date in YYYY-MM-DD
const monthStartEnd = (dStr) => {
  const d = new Date(dStr);
  const y = d.getFullYear();
  const m = d.getMonth();
  const start = new Date(y, m, 1);
  const end = new Date(y, m + 1, 0);
  const iso = (x) => x.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
};

// ---------------- Component ----------------
export default function ManagementReport() {
  const { companyId } = useCompany();

  // Refs used to capture hidden renders for SOFP & Cash Flow pages
  const netChartRef = useRef(null);
  const expChartRef = useRef(null);
  const ytdMixRef = useRef(null);
  const pieYtdRef = useRef(null);
  const pieMonthRef = useRef(null);

  const sofpRef = useRef(null);
  const cfRef = useRef(null);

  // UI state
  const [period, setPeriod] = useState({
    start: `${new Date().getFullYear()}-01-01`,
    end: new Date().toISOString().split("T")[0],
  });
  const [loading, setLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [err, setErr] = useState("");

  // KPIs
  const [kpis, setKpis] = useState({
    totalRevenue: 0,
    totalCOS: 0,
    totalExpense: 0,
    grossProfit: 0,
    netProfit: 0,
    operatingProfit: 0,
  });

  // Ratios
  const [ratios, setRatios] = useState({
    grossMargin: 0,
    netMargin: 0,
    operatingMargin: 0,
  });

  const pieColors = [
    "#008080", // teal (primary)
    "#edb32d", // gold
    "#2f855a", // green
    "#3182ce", // blue
    "#d53f8c", // pink
    "#805ad5", // purple
    "#dd6b20", // orange
    "#4a5568", // slate
  ];

  // Chart states (month labels are short months now)
  const [netMoM, setNetMoM] = useState({ labels: [], values: [] }); // net profit month-on-month (selected period)
  const [expMoM, setExpMoM] = useState({ labels: [], values: [] }); // total expenses month-on-month (selected period)
  const [ytdMix, setYtdMix] = useState({
    labels: [],
    revenue: [],
    cos: [],
    net: [],
  }); // YTD from year start to period.end

  // Top expenses
  const [topExpYtd, setTopExpYtd] = useState({ labels: [], values: [] }); // top 5 YTD
  const [topExpMonth, setTopExpMonth] = useState({ labels: [], values: [] }); // top 5 current month

  // tables for appendix
  const [plTable, setPlTable] = useState([]); // [{desc, type, amt}]
  const [rawItems, setRawItems] = useState([]); // appendix

  // Axis formatter for charts
  const axisTickCb = useCallback((v) => formatCompact(v), []);

  // ---------------- Fetch + compute (journal-only) ----------------
  const fetchAll = useCallback(async () => {
    if (!companyId) return false;

    setLoading(true);
    setErr("");

    try {
      const { start, end } = period;

      // Pull journal entries for selected period (for MoM + KPIs + top month)
      const { data: journal = [], error: jErr } = await supabase
        .from("journal_entries")
        .select(
          "account_id, date, money_in, money_out, chart_of_accounts(name, type)"
        )
        .eq("company_id", companyId)
        .gte("date", start)
        .lte("date", end);

      if (jErr) throw jErr;

      // Pull journal entries for YTD to end (for YTD mix + top YTD)
      const ytdStart = startOfYear(end);
      const { data: journalYtd = [], error: yErr } = await supabase
        .from("journal_entries")
        .select(
          "account_id, date, money_in, money_out, chart_of_accounts(name, type)"
        )
        .eq("company_id", companyId)
        .gte("date", ytdStart)
        .lte("date", end);

      if (yErr) throw yErr;

      // ---- Aggregate P&L table (selected period) by account_id ----
      const tb = {};
      const init = (id, name, type) => {
        if (!tb[id]) tb[id] = { desc: name, type, debit: 0, credit: 0 };
      };

      journal.forEach((e) => {
        const id = e.account_id;
        const name = e.chart_of_accounts?.name || "Unknown";
        const type = e.chart_of_accounts?.type || "Unknown";
        init(id, name, type);
        tb[id].debit += Number(e.money_in) || 0;
        tb[id].credit += Number(e.money_out) || 0;
      });

      const items = Object.values(tb).map((x) => ({
        desc: x.desc,
        type: x.type,
        amt: amountForPL(x.type, x.debit, x.credit),
      }));

      // Totals selected period
      const totals = items.reduce(
        (s, x) => {
          if (x.type === "Revenue") s.rev += Number(x.amt) || 0;
          if (x.type === "Cost of Sales") s.cos += Number(x.amt) || 0;
          if (x.type === "Expense") s.exp += Number(x.amt) || 0;
          return s;
        },
        { rev: 0, cos: 0, exp: 0 }
      );

      const grossProfit = totals.rev - totals.cos;
      const operatingProfit = grossProfit - totals.exp; // (no "other income/expense" currently)
      const netProfit = operatingProfit;

      const grossMargin = totals.rev ? grossProfit / totals.rev : 0;
      const operatingMargin = totals.rev ? operatingProfit / totals.rev : 0;
      const netMargin = totals.rev ? netProfit / totals.rev : 0;

      setKpis({
        totalRevenue: totals.rev,
        totalCOS: totals.cos,
        totalExpense: totals.exp,
        grossProfit,
        operatingProfit,
        netProfit,
      });

      setRatios({ grossMargin, operatingMargin, netMargin });

      setPlTable(
        items
          .filter((r) => isISAccountType(r.type))
          .map((r) => ({ ...r, amt: Number(r.amt || 0) }))
          .sort(
            (a, b) =>
              a.type.localeCompare(b.type) || a.desc.localeCompare(b.desc)
          )
      );
      setRawItems(items);

      // ---- Monthly breakdowns (selected period): Net MoM + Expense MoM ----
      const monthly = {};
      const upsert = (dt, acctType, dr, cr) => {
        const mk = monthKey(dt);
        if (!monthly[mk]) monthly[mk] = { rev: 0, cos: 0, exp: 0 };

        const t = String(acctType || "");
        const debit = Number(dr) || 0;
        const credit = Number(cr) || 0;

        if (t === "Revenue") monthly[mk].rev += credit - debit;
        else if (t === "Cost of Sales") monthly[mk].cos += debit - credit;
        else if (t === "Expense") monthly[mk].exp += debit - credit;
      };

      journal.forEach((e) =>
        upsert(e.date, e.chart_of_accounts?.type, e.money_in, e.money_out)
      );

      const labelsFull = [];
      const labelsShort = [];
      const netVals = [];
      const expVals = [];

      const d0 = new Date(start);
      const d1 = new Date(end);
      const cursor = new Date(d0);
      cursor.setDate(1);

      while (cursor <= d1) {
        const mk = `${cursor.getFullYear()}-${String(
          cursor.getMonth() + 1
        ).padStart(2, "0")}`;
        labelsFull.push(mk);
        labelsShort.push(monthLabelShort(mk));

        const row = monthly[mk] || { rev: 0, cos: 0, exp: 0 };
        const net = (row.rev || 0) - (row.cos || 0) - (row.exp || 0);
        netVals.push(net);

        const exp = row.exp || 0;
        expVals.push(exp);

        cursor.setMonth(cursor.getMonth() + 1);
      }

      setNetMoM({ labels: labelsShort, values: netVals });
      setExpMoM({ labels: labelsShort, values: expVals });

      // ---- YTD mix chart (from year start to period.end): revenue vs COS vs net ----
      const ytdMonthly = {};
      journalYtd.forEach((e) =>
        upsert(e.date, e.chart_of_accounts?.type, e.money_in, e.money_out)
      );

      // rebuild ytdMonthly properly using YTD journal
      // (we used upsert above which writes into `monthly` not ytdMonthly; so do dedicated)
      const upsertYtd = (dt, acctType, dr, cr) => {
        const mk = monthKey(dt);
        if (!ytdMonthly[mk]) ytdMonthly[mk] = { rev: 0, cos: 0, exp: 0 };

        const t = String(acctType || "");
        const debit = Number(dr) || 0;
        const credit = Number(cr) || 0;

        if (t === "Revenue") ytdMonthly[mk].rev += credit - debit;
        else if (t === "Cost of Sales") ytdMonthly[mk].cos += debit - credit;
        else if (t === "Expense") ytdMonthly[mk].exp += debit - credit;
      };

      journalYtd.forEach((e) =>
        upsertYtd(e.date, e.chart_of_accounts?.type, e.money_in, e.money_out)
      );

      const yLabels = [];
      const yLabelsShort = [];
      const yRev = [];
      const yCos = [];
      const yNet = [];

      const yd0 = new Date(ytdStart);
      const yd1 = new Date(end);
      const yCur = new Date(yd0);
      yCur.setDate(1);

      while (yCur <= yd1) {
        const mk = `${yCur.getFullYear()}-${String(
          yCur.getMonth() + 1
        ).padStart(2, "0")}`;
        yLabels.push(mk);
        yLabelsShort.push(monthLabelShort(mk));
        const row = ytdMonthly[mk] || { rev: 0, cos: 0, exp: 0 };
        yRev.push(row.rev || 0);
        yCos.push(row.cos || 0);
        yNet.push((row.rev || 0) - (row.cos || 0) - (row.exp || 0));
        yCur.setMonth(yCur.getMonth() + 1);
      }

      setYtdMix({
        labels: yLabelsShort,
        revenue: yRev,
        cos: yCos,
        net: yNet,
      });

      // ---- Top expenses (Top 5) ----
      // Selected end month
      const { start: mStart, end: mEnd } = monthStartEnd(end);

      // Get expense entries YTD and month by account
      const aggTop = (rows) => {
        const map = new Map(); // name -> amt
        rows.forEach((e) => {
          const t = String(e.chart_of_accounts?.type || "");
          if (t !== "Expense") return;
          const name = e.chart_of_accounts?.name || "Unknown Expense";
          const amt = (Number(e.money_in) || 0) - (Number(e.money_out) || 0); // debit-credit (positive expense)
          map.set(name, (map.get(name) || 0) + amt);
        });
        const sorted = Array.from(map.entries())
          .map(([name, val]) => ({ name, val }))
          .filter((x) => Math.round(x.val * 100) !== 0)
          .sort((a, b) => b.val - a.val)
          .slice(0, 5); // TOP 5
        return {
          labels: sorted.map((x) => x.name),
          values: sorted.map((x) => x.val),
        };
      };

      const topYtd = aggTop(journalYtd);

      // month rows (query journal for end month only, for accuracy)
      const { data: journalMonth = [], error: mErr } = await supabase
        .from("journal_entries")
        .select("date, money_in, money_out, chart_of_accounts(name, type)")
        .eq("company_id", companyId)
        .gte("date", mStart)
        .lte("date", mEnd);

      if (mErr) throw mErr;

      const topM = aggTop(journalMonth);

      setTopExpYtd(topYtd);
      setTopExpMonth(topM);

      return true;
    } catch (e) {
      console.error(e);
      setErr("Failed to compute management report data.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [companyId, period]);

  // ---------------- PDF generation ----------------
  const generateReport = useCallback(async () => {
    if (!companyId) return alert("Select a company first.");
    setGenLoading(true);
    setErr("");

    try {
      const ok = await fetchAll();
      if (!ok) throw new Error("Computation failed.");

      await new Promise((r) => setTimeout(r, 250));

      const doc = new jsPDF({ compress: true });

      // Cover
      doc.setFontSize(20);
      doc.text("Management Account Report", 14, 18);
      doc.setFontSize(12);
      doc.text(`Period: ${period.start} → ${period.end}`, 14, 26);
      doc.addPage();

      // KPI snapshot
      doc.setFontSize(16);
      doc.text("Snapshot (Selected Period)", 14, 18);
      doc.autoTable({
        startY: 26,
        head: [["Metric", "Value"]],
        body: [
          ["Total Revenue", currency(kpis.totalRevenue)],
          ["Gross Profit", currency(kpis.grossProfit)],
          ["Net Profit", currency(kpis.netProfit)],
          ["Total Expenses", currency(kpis.totalExpense)],
        ],
      });

      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 8,
        head: [["Ratio", "Value"]],
        body: [
          ["Gross Profit Margin", `${(ratios.grossMargin * 100).toFixed(2)}%`],
          ["Operating Margin", `${(ratios.operatingMargin * 100).toFixed(2)}%`],
          ["Net Profit Margin", `${(ratios.netMargin * 100).toFixed(2)}%`],
        ],
      });
      doc.addPage();

      // Chart snapshots
      const addChart = async (title, ref, y) => {
        doc.setFontSize(16);
        doc.text(title, 14, 18);
        const canvas = ref.current?.canvas;
        if (canvas) {
          const imgData = await html2canvas(canvas, { scale: 2 }).then((c) =>
            c.toDataURL("image/png")
          );
          doc.addImage(imgData, "PNG", 15, 24, 180, y);
        }
      };

      await addChart("Net Profit — Month-on-Month", netChartRef, 70);
      doc.addPage();
      await addChart("Total Expenses — Month-on-Month", expChartRef, 70);
      doc.addPage();
      await addChart("YTD: Revenue vs COS vs Net Profit", ytdMixRef, 70);
      doc.addPage();
      await addChart("Top 5 Expenses — Year to Date", pieYtdRef, 70);
      doc.addPage();
      await addChart("Top 5 Expenses — Current Month", pieMonthRef, 70);
      doc.addPage();

      // Income Statement table
      doc.setFontSize(16);
      doc.text("Income Statement (Selected Period)", 14, 18);
      doc.autoTable({
        startY: 26,
        head: [["Account", "Type", "Amount (₦)"]],
        body: plTable.map((r) => [r.desc, r.type, currency(r.amt)]),
      });
      doc.addPage();

      // SOFP screenshot
      doc.setFontSize(16);
      doc.text("Statement of Financial Position", 14, 18);
      if (sofpRef.current) {
        const sofpImg = await html2canvas(sofpRef.current, { scale: 2 });
        doc.addImage(sofpImg.toDataURL(), "PNG", 15, 24, 180, 105);
      }
      doc.addPage();

      // Cashflow screenshot
      doc.setFontSize(16);
      doc.text("Cash Flow Statement", 14, 18);
      if (cfRef.current) {
        const cfImg = await html2canvas(cfRef.current, { scale: 2 });
        doc.addImage(cfImg.toDataURL(), "PNG", 15, 24, 180, 105);
      }
      doc.addPage();

      // Appendix
      doc.setFontSize(16);
      doc.text("Appendix: Raw P&L Data", 14, 18);
      doc.autoTable({
        startY: 26,
        head: [["Account", "Type", "Amount (₦)"]],
        body: rawItems.map((r) => [r.desc, r.type, currency(r.amt)]),
      });

      doc.save(`Management_Report_${period.start}_to_${period.end}.pdf`);
    } catch (e) {
      console.error(e);
      setErr("Failed to generate PDF.");
    } finally {
      setGenLoading(false);
    }
  }, [companyId, period, fetchAll, kpis, ratios, plTable, rawItems]);

  // Auto-refresh on date change (keeps existing manual PDF gen too)
  const handleApply = useCallback(async () => {
    await fetchAll();
  }, [fetchAll]);

  // ----- Chart configs (Months as labels + compact y-axis) -----
  const commonLineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: "bottom" } },
      interaction: { mode: "index", intersect: false },
      scales: {
        y: {
          ticks: { callback: axisTickCb },
        },
      },
    }),
    [axisTickCb]
  );

  const commonBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: "bottom" } },
      scales: {
        y: {
          ticks: { callback: axisTickCb },
        },
      },
    }),
    [axisTickCb]
  );

  const commonPieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "bottom" },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.label}: ${currency(ctx.raw)}`,
          },
        },
      },
    }),
    []
  );

  return (
    <div className="mgmt-container">
      <header className="mgmt-header">
        <div>
          <h2>Management Account</h2>
          <p className="subtitle">
            Journal-based insights · Charts + PDF export
          </p>
        </div>

        <div className="filters">
          <label>
            <span>Start</span>
            <input
              type="date"
              value={period.start}
              onChange={(e) =>
                setPeriod((p) => ({ ...p, start: e.target.value }))
              }
            />
          </label>

          <label>
            <span>End</span>
            <input
              type="date"
              value={period.end}
              onChange={(e) =>
                setPeriod((p) => ({ ...p, end: e.target.value }))
              }
            />
          </label>

          <button className="btn" onClick={handleApply} disabled={loading}>
            {loading ? "Applying…" : "Apply Filter"}
          </button>

          <button
            className="btn"
            onClick={generateReport}
            disabled={loading || genLoading}
          >
            {genLoading ? "Generating…" : "Generate PDF"}
          </button>
        </div>
      </header>

      {err && <div className="alert error">{err}</div>}
      {(loading || genLoading) && (
        <div className="loading">Crunching numbers…</div>
      )}

      {/* Snapshot + ratios */}
      <section className="preview">
        <div className="card">
          <h3>Snapshot (Selected Period)</h3>
          <div className="ratio-grid">
            <div className="ratio">
              <span>Total Revenue</span>
              <strong>{currency(kpis.totalRevenue)}</strong>
            </div>
            <div className="ratio">
              <span>Gross Profit</span>
              <strong>{currency(kpis.grossProfit)}</strong>
            </div>
            <div className="ratio">
              <span>Net Profit</span>
              <strong>{currency(kpis.netProfit)}</strong>
            </div>
          </div>

          <h3 style={{ marginTop: 14 }}>Quick Ratios</h3>
          <div className="ratio-grid">
            <div className="ratio">
              <span>Gross Profit Margin</span>
              <strong>{(ratios.grossMargin * 100).toFixed(1)}%</strong>
            </div>
            <div className="ratio">
              <span>Operating Margin</span>
              <strong>{(ratios.operatingMargin * 100).toFixed(1)}%</strong>
            </div>
            <div className="ratio">
              <span>Net Profit Margin</span>
              <strong>{(ratios.netMargin * 100).toFixed(1)}%</strong>
            </div>
          </div>

          {/* ✅ brief definitions requested */}
          <div
            style={{
              marginTop: 12,
              color: "#333",
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            <div>
              <strong>Gross Profit Margin</strong>: How much of revenue remains
              after direct costs (Cost of Sales).
            </div>
            <div>
              <strong>Operating Margin</strong>: Profit from normal operations
              after operating expenses (before financing/taxes, if tracked).
            </div>
            <div>
              <strong>Net Profit Margin</strong>: Final profit as a share of
              revenue after all expenses captured in the period.
            </div>
          </div>
        </div>

        <div className="card">
          <h3>Net Profit — Month-on-Month</h3>
          <div className="chart-wrap">
            <Line
              ref={netChartRef}
              data={{
                labels: netMoM.labels,
                datasets: [
                  { label: "Net Profit", data: netMoM.values, fill: false },
                ],
              }}
              options={{
                ...commonLineOptions,
                scales: {
                  ...commonLineOptions.scales,
                  y: {
                    ...commonLineOptions.scales.y,
                    ticks: {
                      callback: (v) => `₦${formatCompact(v)}`,
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </section>

      {/* Expenses MoM + Top 5 YTD */}
      <section className="preview">
        <div className="card">
          <h3>Total Expenses — Month-on-Month</h3>
          <div className="chart-wrap">
            <Line
              ref={expChartRef}
              data={{
                labels: expMoM.labels,
                datasets: [
                  { label: "Total Expenses", data: expMoM.values, fill: false },
                ],
              }}
              options={{
                ...commonLineOptions,
                scales: {
                  ...commonLineOptions.scales,
                  y: {
                    ...commonLineOptions.scales.y,
                    ticks: {
                      callback: (v) => `₦${formatCompact(v)}`,
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="card">
          <h3>Top 5 Expenses — Year to Date</h3>
          <div className="chart-wrap">
            <Pie
              ref={pieYtdRef}
              data={{
                labels: topExpYtd.labels,
                datasets: [
                  {
                    label: "YTD Expenses",
                    data: topExpYtd.values,
                    backgroundColor: pieColors.slice(
                      0,
                      topExpYtd.values.length
                    ),
                    borderColor: "#ffffff",
                    borderWidth: 2,
                    hoverOffset: 8,
                  },
                ],
              }}
              options={commonPieOptions}
            />
          </div>
        </div>
      </section>

      {/* Top 5 current month + YTD mix */}
      <section className="preview">
        <div className="card">
          <h3>Top 5 Expenses — Current Month</h3>
          <div className="chart-wrap">
            <Pie
              ref={pieMonthRef}
              data={{
                labels: topExpMonth.labels,
                datasets: [
                  {
                    label: "Month Expenses",
                    data: topExpMonth.values,
                    backgroundColor: pieColors.slice(
                      0,
                      topExpYtd.values.length
                    ),
                    borderColor: "#ffffff",
                    borderWidth: 2,
                    hoverOffset: 8,
                  },
                ],
              }}
              options={commonPieOptions}
            />
          </div>
        </div>

        <div className="card">
          <h3>YTD: Revenue vs COS vs Net Profit</h3>
          <div className="chart-wrap">
            <Bar
              ref={ytdMixRef}
              data={{
                labels: ytdMix.labels,
                datasets: [
                  { label: "Revenue", data: ytdMix.revenue },
                  { label: "Cost of Sales", data: ytdMix.cos },
                  { label: "Net Profit", data: ytdMix.net },
                ],
              }}
              options={{
                ...commonBarOptions,
                scales: {
                  ...commonBarOptions.scales,
                  y: {
                    ...commonBarOptions.scales.y,
                    ticks: {
                      callback: (v) => `₦${formatCompact(v)}`,
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </section>

      {/* Hidden region used for PDF screenshots */}
      <div className="hidden-capture">
        <div ref={sofpRef}>
          <BalanceSheet />
        </div>
        <div ref={cfRef}>
          <CashFlows />
        </div>
      </div>
    </div>
  );
}
